import { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { ASC, DESC, VALUATION_DATE_KEY } from 'common/constants/analyst-dashboards';
import { AttioSortDirection } from 'dashboard409a/common/constants/analyst-dashboard';
import ColorSelector from 'dashboard409a/components/ColorSelector/ColorSelector';
import ModelsCalendar from 'dashboard409a/components/ModelsCalendar/ModelsCalendar';
import { ModelsTable } from 'dashboard409a/components/ModelsTable/ModelsTable';
import { modelsStatusOptions } from 'dashboard409a/components/ModelsTable/ModelsTable.types';
import { ModelStatusModal, useModelStatusModal } from 'dashboard409a/components/ModelStatusModal/ModelStatusModal';
import { GenericWorkboard, useNoteHandler } from 'dashboard409a/components/ValuationsTable/common';
import ViewToggle, { ViewType } from 'dashboard409a/components/ViewToggle/ViewToggle';
import { useModelsWithValuationData } from 'dashboard409a/hooks/useModelsWithValuationData';
import { modelStatusMap } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { ModelFilters } from 'dashboard409a/pages/Workboard/Filters';
import { CreateNoteRequest } from 'dashboard409a/services/attio.types';
import { RootStateType } from 'dashboard409a/states/store';
import { getInitialSortFromStorage } from 'dashboard409a/utilities';
import { useLocalStorage } from 'services/hooks';

const MODELS_SORT_KEY = 'modelsSort';
const MODELS_VIEW_KEY = 'modelsWorkboardView';

interface WorkboardModelsTableProps {
  // Filter type: 'current' for non-completed models, 'past' for completed models
  filterType?: 'current' | 'past';
}

const WorkboardModelsTable = ({ filterType = 'current' }: WorkboardModelsTableProps) => {
  // Initialize state from localStorage if available
  const initialSort = getInitialSortFromStorage(MODELS_SORT_KEY, VALUATION_DATE_KEY, DESC);
  const [orderBy, setOrderBy] = useState(initialSort.sortBy);
  const [order, setOrder] = useState<AttioSortDirection>(initialSort.sortOrder);
  const [userIds, setUserIds] = useState<number[]>([]);
  const [searchParam, setSearchParam] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [storedView, setStoredView] = useLocalStorage<ViewType>(MODELS_VIEW_KEY, 'calendar');
  const view: ViewType = (storedView === 'calendar' || storedView === 'table') ? storedView : 'calendar';
  const setView = (newView: ViewType) => {
    setStoredView(newView);
  };

  // Get current user ID from Redux store
  const currentUserId = useSelector((state: RootStateType) => state.user.id);

  // Fetch models data with valuation data
  const {
    rows: allModels,
    isLoading,
    updateModelNote,
    updateModelStatus,
    updateModelDueDate,
  } = useModelsWithValuationData({
    filterType,
    userIds,
    orderBy,
    order,
  });

  const models = useMemo(() => {
    let filtered = [...allModels];

    // Filter by search param (company name)
    if (searchParam.trim()) {
      const searchLower = searchParam.toLowerCase().trim();
      filtered = filtered.filter(model =>
        model.company_name?.toLowerCase().includes(searchLower)
      );
    }

    // Filter by status
    if (searchStatus && searchStatus !== '' && searchStatus !== 'All') {
      const targetStatus = modelStatusMap[searchStatus as keyof typeof modelStatusMap];
      if (targetStatus !== undefined) {
        // Compare numeric values directly (model.model_status is a number, targetStatus is also a number)
        filtered = filtered.filter(model => model.model_status === targetStatus);
      }
    }

    // Filter by date range (supporting_due_date)
    if (fromDate) {
      filtered = filtered.filter(model => {
        if (!model.supporting_due_date) {
          return false;
        }
        const modelDate = new Date(model.supporting_due_date);
        return modelDate >= fromDate;
      });
    }

    if (toDate) {
      filtered = filtered.filter(model => {
        if (!model.supporting_due_date) {
          return false;
        }
        const modelDate = new Date(model.supporting_due_date);
        // Set to end of day for inclusive comparison
        const toDateEnd = new Date(toDate);
        toDateEnd.setHours(23, 59, 59, 999);
        return modelDate <= toDateEnd;
      });
    }

    return filtered;
  }, [allModels, searchParam, searchStatus, fromDate, toDate]);

  // Use the generic note handler
  const {
    isNoteLoading,
    noteError,
    handleNoteSave: handleNoteSaveGeneric,
  } = useNoteHandler({
    onSuccess: (id, noteContent, createdNote) => {
      // If a note was just created, use the fulfillment from the result
      if (createdNote?.fulfillment) {
        const fulfillmentId = createdNote.fulfillment;
        // Update the note with the real ID from the created note
        const noteId = createdNote.id;
        updateModelNote(fulfillmentId, noteContent, noteId);
      } else {
        // For updates, use the id parameter (which is the fulfillmentId)
        updateModelNote(id as number, noteContent);
      }
    },
  });

  const handleNoteSave = async (fulfillment: number, noteContent: string, currentNote?: string) => {
    // Validate fulfillment_id
    if (!fulfillment) {
      throw new Error('Invalid fulfillment ID. Cannot save note.');
    }

    const request: CreateNoteRequest = {
      content: noteContent,
      fulfillment,
      reference_type: 'fu',
    };

    // Determine if we're updating an existing note or creating a new one
    const currentModel = models.find(m => m.id === fulfillment);
    const hasExistingNote = Boolean(
      (currentNote && currentNote.trim() !== '' && currentNote !== '-') ||
        (currentModel?.note && currentModel.note.content && currentModel.note.content.trim() !== '')
    );

    let existingNoteId: string | undefined;

    if (hasExistingNote) {
      // Check if the note has a valid ID (not 0, undefined, or null)
      const noteId = currentModel?.note?.id;
      if (noteId && noteId !== 0) {
        existingNoteId = noteId.toString();
      } else {
        // If note ID is invalid (0 or undefined), treat as new note
        existingNoteId = undefined;
      }
    }

    await handleNoteSaveGeneric(fulfillment, noteContent, currentNote, request, hasExistingNote, existingNoteId);
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? DESC : ASC);
    setOrderBy(property);
  };

  const handleStatusUpdate = (modelId: number, newStatus: number) => {
    updateModelStatus(modelId, newStatus);
  };

  const handleFiltersChange = useCallback((filters: {
    searchParam: string;
    searchStatus: string;
    fromDate: Date | null;
    toDate: Date | null;
    userIds: number[];
  }) => {
    setUserIds(filters.userIds);
    setSearchParam(filters.searchParam);
    setSearchStatus(filters.searchStatus);
    setFromDate(filters.fromDate);
    setToDate(filters.toDate);
  }, []);

  const statusOptionsLoading = false;
  const stableOnFiltersChange = useMemo(() => handleFiltersChange, [handleFiltersChange]);

  const {
    openModal,
    selectedModel,
    handleOpenModal,
    handleCloseModal,
    handleCreateModelSupport,
    isCreatingModelSupport,
  } = useModelStatusModal({ models });

  const handleEventDrop = async (modelId: number, newDueDate: string) => {
    // Optimistic update - update UI immediately
    updateModelDueDate(modelId, newDueDate);
  };

  return (
    <GenericWorkboard>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pr: 2 }}>
        <ModelFilters
          statusOptions={modelsStatusOptions}
          statusOptionsLoading={statusOptionsLoading}
          onFiltersChange={stableOnFiltersChange}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ColorSelector />
          <ViewToggle view={view} onChange={setView} />
        </Box>
      </Box>
      {view === 'calendar' ? (
        <>
          <ModelsCalendar
            models={models}
            isLoading={isLoading}
            onOpenPreview={handleOpenModal}
            onEventDrop={handleEventDrop}
            userId={currentUserId?.toString() || ''}
          />
        </>
      ) : (
        <ModelsTable
          isLoading={isLoading}
          rows={models}
          onNoteSave={handleNoteSave}
          orderBy={orderBy}
          order={order}
          onRequestSort={handleRequestSort}
          onStatusUpdate={handleStatusUpdate}
          isNoteLoading={isNoteLoading}
          noteError={noteError}
          onRowClick={handleOpenModal}
        />
      )}

      <ModelStatusModal
        open={openModal}
        onClose={handleCloseModal}
        selectedModel={selectedModel}
        isLoading={isLoading}
        onCreateModelSupport={handleCreateModelSupport}
        isCreatingModelSupport={isCreatingModelSupport}
      />
    </GenericWorkboard>
  );
};

export default WorkboardModelsTable;
